import { useState, useEffect, useCallback, useMemo } from "react";
import { Room, RoomEvent, Track } from "livekit-client";


const participantInitData = {
  userdata: {},
  audioTrack: null,
  videoTrack: null,
  screenShareTrack: null,
  isAudioEnabled: false,
  isVideoEnabled: false,
}

export function useLivekitRoom({ serverUrl }) {

  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState({});
  const [localParticipant, setLocalParticipant] = useState(participantInitData)
  const [foolScreen, _setFoolScreen] = useState(null)

  const closeFoolScreen = () => _setFoolScreen(null)

  const setFoolScreen = (user) => _setFoolScreen(user)

  const joinRoom = useCallback(async (token, mediaParams) => {
    
    const newRoom = new Room();
    setRoom(newRoom);

    newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      const { identity, participantInfo } = participant
      setParticipants(prev => {
        
        const current = prev[identity] || participantInitData;
        let updated = { ...current };

        if (publication.source === Track.Source.Camera) {
          updated.videoTrack = track
          updated.isVideoEnabled = true
        } else if (publication.source === Track.Source.Microphone) {
          updated.audioTrack = track
          updated.isAudioEnabled = true
        } else if (publication.source === Track.Source.ScreenShare) {
          updated.screenShareTrack = track
        }
        updated.userdata = participantInfo.attributes

        return {...prev, [identity]: updated};
      });
    });

    newRoom.on(RoomEvent.TrackUnsubscribed, (_track, publication, participant) => {
      const identity = participant.identity;

      setParticipants(prev => {
        const current = prev[identity];
        if (!current) return prev;

        let updated = { ...current };

        if (publication.source === Track.Source.Camera) {
          updated.videoTrack = null;
        } else if (publication.source === Track.Source.Microphone) {
          updated.audioTrack = null;
        } else if (publication.source === Track.Source.ScreenShare) {
          updated.screenShareTrack = null;
        }

        if (!updated.videoTrack && !updated.audioTrack && !updated.screenShareTrack) {
          const { [identity]: _, ...rest } = prev;
          return rest;
        }

        return {
          ...prev,
          [identity]: updated,
        };
      });
    });


    newRoom.on(RoomEvent.LocalTrackPublished, (publication, participant) => {
      if (publication.source === Track.Source.ScreenShare) {
        setLocalParticipant(prev => ({ ...prev, screenShareTrack: publication.track }));
      }
    });

    newRoom.on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
      if (publication.source === Track.Source.ScreenShare) {
        setLocalParticipant(prev => ({ ...prev, screenShareTrack: null }));
      }
    });


    await newRoom.connect(serverUrl, token);

    // await newRoom.localParticipant.enableCameraAndMicrophone();
    const { videoDeviceId, audioDeviceId } = mediaParams
    if (videoDeviceId) {
      await newRoom.localParticipant.setCameraEnabled(true, { deviceId: videoDeviceId });
    }
    if (audioDeviceId) {
      await newRoom.localParticipant.setMicrophoneEnabled(true, { deviceId: audioDeviceId });
    }
    
    const {audioTrackPublications, videoTrackPublications, attributes} = newRoom.localParticipant

    const audioPub = Array.from(audioTrackPublications.values())[0];
    const videoPub = Array.from(videoTrackPublications.values()).find(p => p.source === Track.Source.Camera);

    const audioTrack = audioPub?.track || null;
    const videoTrack = videoPub?.track || null;

    setLocalParticipant(prev => ({
      ...prev,
      videoTrack,
      audioTrack,
      isAudioEnabled: !!audioTrack,
      isVideoEnabled: !!videoTrack,
      userdata: attributes
    }))
    

    return newRoom;
  }, [serverUrl]);


  const toggleMuteParticipant = (participantId) => {
    setParticipants(prev => ({
      ...prev,
      [participantId]: {
        ...prev[participantId],
        isAudioEnabled: !prev[participantId].isAudioEnabled
      }
    }))
  }


  const leaveRoom = useCallback(async () => {
    if (room) {
      if (localParticipant.screenShareTrack) {
        await room.localParticipant.setScreenShareEnabled(false);
      }
      await room.disconnect();
    }
    setRoom(null);
    setParticipants({});
    setLocalParticipant(participantInitData)
  }, [room, localParticipant]);



  const toggleAudio = useCallback(async () => {
    if (!room) return;
    const pub = Array.from(room.localParticipant.audioTrackPublications.values())[0];
    if (!pub) return;

    const isAudioEnabled = !localParticipant.isAudioEnabled

    if (isAudioEnabled) {
      await pub.unmute();
    } else {
      await pub.mute();
    }

    setLocalParticipant(prev => ({...prev, isAudioEnabled}))

  }, [room, localParticipant]);



  const toggleVideo = useCallback(async () => {
    if (!room) return;
    const pub = Array.from(room.localParticipant.videoTrackPublications.values()).find(
      p => p.source === Track.Source.Camera
    );
    if (!pub) return;

    const isVideoEnabled = !localParticipant.isVideoEnabled

    if (isVideoEnabled) {
      await pub.unmute();
    } else {
      await pub.mute();
    }

    setLocalParticipant(prev => ({...prev, isVideoEnabled}))
  }, [room, localParticipant]);



  const toggleScreenShare = useCallback(async () => {
    if (!room) return;

    try {

      const willShare = !localParticipant.screenShareTrack;
      await room.localParticipant.setScreenShareEnabled(willShare);

    } catch (error) {
      console.error("Screen share failed:", error);
    }
  }, [room, localParticipant]);


  useEffect(() => {
    return () => {
      if (room) {
        room.disconnect();
      }
    };
  }, [room]);

  return {
    room,
    localParticipant, 
    participants,
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
    toggleMuteParticipant,
    foolScreen,
    setFoolScreen, 
    closeFoolScreen
  };
}